import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import RemoveDialog from '../../common/components/RemoveDialog';
import { useTranslation } from '../../common/components/LocalizationProvider';

const useStyles = makeStyles()(() => ({
  row: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const CollectionActions = ({
  itemId,
  editPath,
  endpoint,
  setTimestamp,
  customActions = [],
  readonly = false,
  disableRemove = false,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const t = useTranslation();

  const phone = useMediaQuery(theme.breakpoints.down('sm'));

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [removing, setRemoving] = useState(false);

  const normalizedCustomActions = useMemo(
    () => (Array.isArray(customActions) ? customActions.filter(Boolean) : []),
    [customActions],
  );

  const canEdit = Boolean(editPath) && !readonly;
  const canRemove = !readonly && !disableRemove;

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleEdit = () => {
    if (!canEdit) {
      handleCloseMenu();
      return;
    }

    navigate(`${editPath}/${itemId}`);
    handleCloseMenu();
  };

  const handleRemove = () => {
    if (!canRemove) {
      handleCloseMenu();
      return;
    }

    setRemoving(true);
    handleCloseMenu();
  };

  const handleCustom = (action) => {
    if (!action?.handler) {
      handleCloseMenu();
      return;
    }

    action.handler(itemId);
    handleCloseMenu();
  };

  const handleRemoveResult = (removed) => {
    setRemoving(false);

    if (removed && typeof setTimestamp === 'function') {
      setTimestamp(Date.now());
    }
  };

  return (
    <>
      {phone ? (
        <>
          <IconButton size="small" onClick={handleOpenMenu}>
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            open={Boolean(menuAnchorEl)}
            anchorEl={menuAnchorEl}
            onClose={handleCloseMenu}
          >
            {normalizedCustomActions.map((action) => (
              <MenuItem onClick={() => handleCustom(action)} key={action.key}>
                {action.title}
              </MenuItem>
            ))}

            {canEdit && (
              <MenuItem onClick={handleEdit}>
                {t('sharedEdit') || 'Editar'}
              </MenuItem>
            )}

            {canRemove && (
              <MenuItem onClick={handleRemove}>
                {t('sharedRemove') || 'Excluir'}
              </MenuItem>
            )}
          </Menu>
        </>
      ) : (
        <div className={classes.row}>
          {normalizedCustomActions.map((action) => (
            <Tooltip title={action.title} key={action.key}>
              <IconButton size="small" onClick={() => handleCustom(action)}>
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}

          {canEdit && (
            <Tooltip title={t('sharedEdit') || 'Editar'}>
              <IconButton size="small" onClick={handleEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {canRemove && (
            <Tooltip title={t('sharedRemove') || 'Excluir'}>
              <IconButton size="small" onClick={handleRemove}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}

      <RemoveDialog
        style={{ transform: 'none' }}
        open={removing}
        endpoint={endpoint}
        itemId={itemId}
        onResult={handleRemoveResult}
      />
    </>
  );
};

CollectionActions.propTypes = {
  itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  editPath: PropTypes.string,
  endpoint: PropTypes.string.isRequired,
  setTimestamp: PropTypes.func,
  customActions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.node,
      handler: PropTypes.func,
    }),
  ),
  readonly: PropTypes.bool,
  disableRemove: PropTypes.bool,
};

export default CollectionActions;